/**
 * Which language version of a guide video to play. Mirrors the website's
 * `blueticks-website/apps/blueticks/src/lib/content-language.ts`, with the
 * app's own signals in place of the website's geo cookie:
 *
 *   1. an explicit video-language choice (localStorage `bt_content_lang`)
 *   2. the user's explicit UI language (`user.preferredLanguage`), pt / ar / es only
 *   3. country: `user.countryCode` || the region of navigator.language
 *      (PT-speaking → pt, the 22 Arab League members → ar)
 *   4. navigator.languages
 *   5. English
 *
 * The result is always one of the languages the video actually has.
 */

export type ContentLang = 'en' | 'pt' | 'ar' | 'es';

export const CONTENT_LANGS: ContentLang[] = ['en', 'pt', 'ar', 'es'];
export const CONTENT_LANG_STORAGE_KEY = 'bt_content_lang';

const PORTUGUESE_COUNTRIES = new Set(['BR', 'PT', 'AO', 'MZ', 'CV', 'GW', 'ST', 'TL']);
// The 22 Arab League members (incl. DJ and KM, which the UI-language detector maps to fr).
const ARABIC_COUNTRIES = new Set([
  'SA', 'AE', 'EG', 'MA', 'DZ', 'TN', 'LY', 'SD', 'IQ', 'JO', 'LB',
  'SY', 'PS', 'KW', 'QA', 'BH', 'OM', 'YE', 'MR', 'SO', 'DJ', 'KM',
]);

/** 'pt-BR' → 'pt', 'ar-SA' → 'ar', 'es-ES' → 'es', 'en-US' → 'en', 'he-IL' → null. */
export function toContentLang(code: string | null | undefined): ContentLang | null {
  const base = (code ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return (CONTENT_LANGS as string[]).includes(base) ? (base as ContentLang) : null;
}

export function langForCountry(country: string | null | undefined): ContentLang | null {
  const cc = (country ?? '').trim().toUpperCase();
  if (PORTUGUESE_COUNTRIES.has(cc)) return 'pt';
  if (ARABIC_COUNTRIES.has(cc)) return 'ar';
  return null;
}

/** 'pt-BR' → 'BR', 'en' → ''. */
export function regionOf(locale: string | null | undefined): string {
  const parts = (locale ?? '').split(/[-_]/);
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
}

export interface GuideLangSignals {
  stored?: string | null;
  preferredLanguage?: string | null;
  country?: string | null;
  languages?: readonly string[];
}

export function resolveGuideVideoLang(available: readonly ContentLang[], signals: GuideLangSignals): ContentLang {
  const ok = (l: ContentLang | null): l is ContentLang => !!l && available.includes(l);
  const stored = toContentLang(signals.stored);
  if (ok(stored)) return stored;
  const preferred = toContentLang(signals.preferredLanguage);
  if (preferred && preferred !== 'en' && ok(preferred)) return preferred;
  const byCountry = langForCountry(signals.country);
  if (ok(byCountry)) return byCountry;
  for (const code of signals.languages ?? []) {
    const l = toContentLang(code);
    if (ok(l)) return l;
  }
  return available.includes('en') ? 'en' : (available[0] ?? 'en');
}

/** The browser half of the signals (the user half comes from the store). */
export function readBrowserLangSignals(): Pick<GuideLangSignals, 'stored' | 'languages'> & { navigatorRegion: string } {
  let stored: string | null = null;
  try {
    stored = globalThis.localStorage?.getItem(CONTENT_LANG_STORAGE_KEY) ?? null;
  } catch {
    /* storage blocked */
  }
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const languages = nav ? (nav.languages?.length ? nav.languages : [nav.language]) : [];
  return { stored, languages, navigatorRegion: regionOf(nav?.language) };
}
